import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('hw:card-option-changed', this.#handleHwOptionChanged);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('hw:card-option-changed', this.#handleHwOptionChanged);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles hw:card-option-changed events dispatched by hw-size-picker.
   * Fired when the size picker (or swatches coordinating via size picker) resolves a variant.
   * Updates price and gallery image without touching the browser URL bar.
   * @param {CustomEvent} event
   */
  #handleHwOptionChanged = (event) => {
    event.stopPropagation();
    const { variant, userInitiated } = event.detail;
    if (!variant) return;

    // Dispatch VariantUpdateEvent so product-form-component updates its hidden variant ID input.
    // This also triggers #handleVariantUpdate (price, ATC, side-effects).
    this.dispatchEvent(
      new VariantUpdateEvent(
        {
          id: variant.id,
          available: variant.available,
          featured_media: variant.featured_media,
          price: variant.price,
          compare_at_price: variant.compare_at_price,
          price_formatted: variant.price_formatted,
          compare_at_price_formatted: variant.compare_at_price_formatted,
          show_compare: variant.show_compare,
          keep_from_on_init: userInitiated !== true,
        },
        '',
        { html: null, productId: this.dataset.productId ?? '' }
      )
    );

    // Navigate slideshow to the variant's slide (not handled by #handleVariantUpdate).
    const mediaId = variant.featured_media?.id;
    if (mediaId && mediaId !== 'null' && this.refs.slideshow) {
      this.refs.slideshow.select({ id: String(mediaId) }, undefined, { animate: false });
    }
  };

  /**
   * Handles the variant update event.
   * When data.html is present (section rendering), morph price and picker and read URL from HTML.
   * When data.html is null (JS-only from swatches), update link, image, and price from event.detail.resource.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    const { resource, data } = event.detail;
    const hasHtml = data.html != null;

    if (hasHtml) {
      this.updatePrice(event);
      this.#isUnavailableVariantSelected(event);
      this.#updateProductUrl(event);
      if (event.target !== this.variantPicker) {
        this.variantPicker?.updateVariantPicker(data.html);
      }
    } else if (resource) {
      this.#updateFromVariantOnly(resource);

      // product-form.js:#onVariantUpdate assumes data.html is non-null at line 526.
      // Since we never do section-rendering on product cards, html is always null here.
      // Stop other listeners (product-form-component) from receiving this event,
      // and update the hidden variant ID input directly instead.
      event.stopImmediatePropagation();
      const variantIdInput = /** @type {HTMLInputElement|null} */ (this.querySelector('input[name="id"]'));
      if (variantIdInput) variantIdInput.value = String(resource.id);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
    this.removeAttribute('data-no-swatch-selected');
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    this.#updateOverflowList();
  };

  /**
   * Updates link, price, and add-to-cart state from variant data (no section-rendering HTML).
   * @param {Object} variant - Variant object with id, available, featured_media, price_formatted, compare_at_price_formatted, show_compare
   */
  #updateFromVariantOnly(variant) {
    const productUrl = (this.refs.productCardLink?.href || '').split('?')[0];
    if (!productUrl) return;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variant.id);
    const href = url.toString();

    if (this.refs.productCardLink) this.refs.productCardLink.href = href;
    const cardGalleryLink = this.refs.cardGalleryLink;
    if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = href;
    const productTitleLink = this.refs.productTitleLink;
    if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = href;

    this.#updatePriceFromVariant(variant);
    this.#toggleAddToCartButton(variant.available);
  }

  /**
   * Updates the price container from variant data (price_formatted, compare_at_price_formatted, show_compare).
   * @param {Object} variant
   */
  #updatePriceFromVariant(variant) {
    const priceContainers = this.querySelectorAll('product-price [ref="priceContainer"]');
    const priceContainer = priceContainers[1] ?? priceContainers[0];
    if (!priceContainer) return;

    // Keep server-rendered "From …" on initial card auto-selection.
    // User interactions (swatch/size clicks) still replace with exact variant price.
    const shouldKeepFromOnInit =
      variant.keep_from_on_init === true && priceContainer.dataset.shouldShowFromPrice === 'true';
    if (shouldKeepFromOnInit) return;

    const priceEl = priceContainer.querySelector('.price');
    const compareEl = priceContainer.querySelector('.compare-at-price');
    if (priceEl) priceEl.textContent = variant.price_formatted ?? '';
    if (variant.show_compare && variant.compare_at_price_formatted != null) {
      if (compareEl) {
        compareEl.textContent = variant.compare_at_price_formatted;
        compareEl.hidden = false;
      }
    } else if (compareEl) {
      compareEl.textContent = '';
      compareEl.hidden = true;
    }
  }

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    // Base theme: button lives inside .add-to-cart__button
    const addToCartButton = this.querySelector('.add-to-cart__button button');
    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
      return;
    }

    // hw-add-to-cart: button is inside <add-to-cart-component> which exposes enable()/disable()
    const addToCartComponent = /** @type {any} */ (this.querySelector('add-to-cart-component'));
    if (addToCartComponent) {
      if (enable) {
        addToCartComponent.enable?.();
      } else {
        addToCartComponent.disable?.();
      }
      // Also directly toggle the button in case enable()/disable() aren't defined
      const btn = /** @type {HTMLButtonElement|null} */ (addToCartComponent.querySelector('button[ref="addToCartButton"], button[name="add"]'));
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = !enable;
      }
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Get variants data from the in-page JSON (for JS-only updates when inside a product card).
   * @returns {Array<{id: number, price: number, compare_at_price: number, available: boolean, featured_media: {id: number|null}, price_formatted: string, compare_at_price_formatted: string, show_compare: boolean}>}
   */
  get variantsFromJson() {
    const script = this.querySelector('script[data-variants]');
    if (!script?.textContent) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  /**
   * Override the variantChanged method. When inside a product-card, do not use the section
   * rendering API; resolve the variant from in-page JSON and dispatch VariantUpdateEvent with
   * variant-only data so the card updates link, image, and price in JS.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    if (!selectedOption) return;

    const isInsideProductCard = this.closest('product-card');
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');

    if (isInsideProductCard && isSwatchInput) {
      event.stopPropagation();
      this.updateSelectedOption(event.target);

      // When an hw-size-picker is present, hand off variant resolution to it.
      // The size picker listens for hw:card-option-changed and will re-evaluate
      // which size+color combination is best, then dispatch back with a full variant.
      const sizePicker = isInsideProductCard.querySelector('hw-size-picker');

      if (sizePicker) {
        const inputId = selectedOption.dataset.inputId || '';
        const optionPosition = parseInt(inputId.split('-')[0], 10);
        const value = selectedOption.value;

        if (!isNaN(optionPosition) && value) {
          let state = {};
          try { state = JSON.parse(isInsideProductCard.dataset.hwOptions || '{}'); } catch { /* noop */ }
          state[optionPosition] = value;
          isInsideProductCard.dataset.hwOptions = JSON.stringify(state);

          // Notify size picker — it will resolve the variant and fire back with one
          isInsideProductCard.dispatchEvent(
            new CustomEvent('hw:card-option-changed', {
              bubbles: false,
              detail: { optionPosition, value },
            })
          );
        }
        return;
      }

      // No size picker — standard swatch-only variant resolution
      const variantId =
        selectedOption.dataset.firstAvailableOrFirstVariantId || selectedOption.dataset.variantId || null;
      if (!variantId) return;

      const variants = this.variantsFromJson;
      const variant = variants.find((v) => String(v.id) === String(variantId));
      if (!variant) return;

      const productUrl = (this.dataset.productUrl || '').split('?')[0];
      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);

      this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));
      this.dispatchEvent(
        new VariantUpdateEvent(
          {
            id: variant.id,
            available: variant.available,
            featured_media: variant.featured_media,
            price: variant.price,
            compare_at_price: variant.compare_at_price,
            price_formatted: variant.price_formatted,
            compare_at_price_formatted: variant.compare_at_price_formatted,
            show_compare: variant.show_compare,
          },
          this.selectedOptionId || '',
          { html: null, productId: this.dataset.productId ?? '' }
        )
      );
      // history.replaceState intentionally omitted: product cards appear on
      // non-product pages (collection, home) so the browser URL should not change.
      return;
    }

    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
